/**
 * Message formatting and Intl formatters per locale (no React, so tests can check plurals, dates,
 * numbers and money in every language). Formatters are created once per locale.
 */
import { en, type MessageKey } from './i18n-catalog.ts';
export type { MessageKey };

export type Locale = 'en' | 'ru' | 'ko';
/** English is always present; Russian and Korean are added when first chosen. */
const catalogs: Partial<Record<Locale, Partial<Record<MessageKey, string>>>> = { en };
const loading = new Map<Locale, Promise<void>>();
// One import per language: a single conditional import would make the bundler preload both files.
const loaders: Record<Exclude<Locale, 'en'>, () => Promise<Partial<Record<MessageKey, string>>>> = {
  ru: () => import('./i18n-ru.ts').then(m => m.ruAll),
  ko: () => import('./i18n-ko.ts').then(m => m.koAll),
};
export const isLocaleLoaded = (locale: Locale) => catalogs[locale] !== undefined;
/** Loads a language's messages once; a failed load can be retried and meanwhile shows English. */
export function loadLocale(locale: Locale): Promise<void> {
  if (catalogs[locale]) return Promise.resolve();
  let pending = loading.get(locale);
  if (!pending) {
    pending = loaders[locale as Exclude<Locale, 'en'>]()
      .then(catalog => { catalogs[locale] = catalog; })
      .finally(() => loading.delete(locale));
    loading.set(locale, pending);
  }
  return pending;
}

export interface I18n {
  locale: Locale;
  t: (key: MessageKey, values?: Record<string, string | number>) => string;
  /** A server-written reason or failure code under `prefix`; an unknown one reads "Other reason (code)", never a raw key. */
  reason: (prefix: string, code: string | null | undefined, values?: Record<string, string | number>) => string;
  /** Calendar dates stored as YYYY-MM-DD (formatted in UTC so the day never shifts). */
  date: Intl.DateTimeFormat;
  /** Instants (timestamptz) in the viewer's time zone. */
  localDate: Intl.DateTimeFormat;
  shortDate: Intl.DateTimeFormat;
  dateTime: Intl.DateTimeFormat;
  time: Intl.DateTimeFormat;
  number: Intl.NumberFormat;
  money: (minor: number, currency: string) => string;
  collator: Intl.Collator;
}

/** Minimal ICU subset: {name} and {n, plural, =0 {...} one {...} few {...} other {...}} with #; an exact =N case wins over the category. */
export function format(pattern: string, values: Record<string, string | number>, plural: Intl.PluralRules, number: Intl.NumberFormat): string {
  return pattern.replace(/\{(\w+), plural, ((?:=?\w+ \{[^}]*\}\s*)+)\}|\{(\w+)\}/g, (_m, pName: string, forms: string, name: string) => {
    if (name) return String(values[name] ?? '');
    const n = Number(values[pName] ?? 0);
    const options = Object.fromEntries([...forms.matchAll(/(=?\w+) \{([^}]*)\}/g)].map(x => [x[1], x[2]]));
    const chosen = options[`=${n}`] ?? options[plural.select(n)] ?? options.other ?? '';
    return chosen.replaceAll('#', number.format(n));
  });
}

export function createI18n(locale: Locale): I18n {
  const plural = new Intl.PluralRules(locale);
  const number = new Intl.NumberFormat(locale);
  const moneyFormats = new Map<string, Intl.NumberFormat>();
  return {
    locale,
    // A missing message shows its key instead of breaking the page (records and codes from a
    // newer server can arrive before the client knows their names).
    t: (key, values = {}) => format(catalogs[locale]?.[key] ?? en[key] ?? String(key), values, plural, number),
    reason: (prefix, code, values = {}) => {
      const key = `${prefix}.${String(code)}` as MessageKey;
      const known = catalogs[locale]?.[key] ?? en[key];
      return known !== undefined ? format(known, { reason: String(code), ...values }, plural, number)
        : format(catalogs[locale]?.['state.otherReason'] ?? en['state.otherReason'], { code: String(code) }, plural, number);
    },
    date: new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }),
    localDate: new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'short', day: 'numeric' }),
    shortDate: new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }),
    dateTime: new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }),
    time: new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }),
    number,
    money: (minor, currency) => {
      // Whole amounts show no cents ($1,200); others keep them ($1,200.50).
      const whole = minor % 100 === 0;
      const key = `${currency}:${whole}`;
      let f = moneyFormats.get(key);
      if (!f) { f = new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2 }); moneyFormats.set(key, f); }
      return f.format(minor / 100);
    },
    collator: new Intl.Collator(locale, { sensitivity: 'base', numeric: true }),
  };
}

